import { createRootRoute, createRoute, Outlet, redirect } from "@tanstack/react-router";
import { Suspense } from "react";

import { DataPath } from "../screens/DataPath";
import { SiteEquipment } from "../screens/SiteEquipment";
import { Organisations } from "../screens/Organisations";
import { People } from "../screens/People";
import { Configuration } from "../screens/Configuration";
import { Onboarding } from "../screens/Onboarding";
import { Admin } from "../screens/Admin";
import { Assets } from "../screens/Assets";
import { Home } from "../screens/Home";
import { Kpi } from "../screens/Kpi";
import { Plants } from "../screens/Plants";
import { Preferences } from "../screens/Preferences";
import { ScopeData } from "../screens/ScopeData";
import { SiteNow } from "../screens/SiteNow";
import { SiteWeatherForecast } from "../screens/SiteWeatherForecast";
import { SiteSettings } from "../screens/SiteSettings";
import { SitePerformance } from "../screens/SitePerformance";
import { PortfolioWaterfall } from "../screens/PortfolioWaterfall";
import { Reports } from "../screens/Reports";
// Disha's lane screens (P3 blocks + the actions/shell screens), hosted in the shell.
import { SiteBlocksScreen } from "../screens/SiteBlocks";
import { SiteStringsScreen } from "../screens/SiteStrings";
import { ExplorerView } from "../screens/ExplorerView";
import { BlockDetail } from "../screens/BlockDetail";
import { Branding } from "../screens/Branding";
import { InterventionsScreen } from "../screens/Interventions";
import { NotificationsScreen } from "../screens/Notifications";
import { GlossaryScreen } from "../screens/Glossary";
import { SearchScreen } from "../screens/Search";
import { AlertsList } from "../screens/AlertsList";
import { LockedFamilyScreen } from "../screens/LockedFamily";
import { RootLayout } from "../shell/RootLayout";
import {
  validateTimeSearch,
  validateFleetSearch,
  validateEquipmentSearch,
  validateConfigurationSearch,
} from "./search";
import { lazyScreen } from "./lazyScreen";

// The plant schematic pulls in elkjs (~450 KB); code-split it so it loads only when
// the schematic is opened, keeping it out of the initial bundle.
// `lazyScreen` rather than `lazy`: a chunk whose name no longer exists because we
// have deployed since this tab loaded is a stale tab, not a broken screen, so it
// reloads once instead of showing an error.
// P8 is the DYNAMIC single line now (sir's call): the same two reads the static
// schematic made — the asset graph and the live `now` feed — drawn as a live
// single line with flow marks. `PlantSchematic` is kept, unrouted, as the fallback
// renderer until the dynamic one has been through a demo.
const DynamicSLD = lazyScreen(() =>
  import("../screens/DynamicSLD").then((m) => ({ default: m.DynamicSLD })),
);
// Meters & sensors pulls in uPlot; code-split it out of the initial bundle too.
const SensorView = lazyScreen(() =>
  import("../screens/SensorView").then((m) => ({ default: m.SensorView })),
);

export const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  validateSearch: validateTimeSearch,
  component: HomeScreen,
});

function HomeScreen() {
  const search = indexRoute.useSearch();
  // from/to travel with the period, or the date navigator moves the URL and the
  // screen keeps serving the same day.
  return (
    <Home
      period={"period" in search ? search.period : undefined}
      from={"from" in search ? search.from : undefined}
      to={"to" in search ? search.to : undefined}
      mode={"mode" in search ? search.mode : undefined}
    />
  );
}

// Scope layout: /t/$tenant/s/$scope — a saved view is a URL.
const scopeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/t/$tenant/s/$scope",
  component: () => <Outlet />,
});

// `/t/$tenant/s/$scope` with nothing after it MATCHED (the layout route has no
// path of its own to fail on), but `Outlet` then found no child and rendered
// nothing — so trimming "/plants" off the URL gave the shell with a completely
// empty content area rather than a screen or a not-found (QA, 25 Oct). The scope's
// landing screen is the fleet grid. `replace` so the bare URL does not sit in
// history as a step the back button returns to.
const scopeIndexRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    // The validator is the single source of the fleet grid's defaults, so the
    // redirect lands on exactly the same state a plain /plants visit would.
    throw redirect({
      to: "/t/$tenant/s/$scope/plants",
      params,
      search: validateFleetSearch({}),
      replace: true,
    });
  },
});

const plantsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants",
  validateSearch: validateFleetSearch,
  component: PlantsScreen,
});

function PlantsScreen() {
  const { tenant, scope } = plantsRoute.useParams();
  const search = plantsRoute.useSearch();
  return (
    <Plants
      tenant={tenant}
      scope={scope}
      period={search.period}
      from={search.from}
      to={search.to}
      view={search.view}
      sort={search.sort}
      filter={search.filter}
      group={search.group}
      density={search.density}
    />
  );
}

// P2 — Site: Now. Demonstrates typed URL state (?period=&mode=).
export const siteNowRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/now",
  validateSearch: validateTimeSearch,
  component: SiteNowScreen,
});

function SiteNowScreen() {
  const { site } = siteNowRoute.useParams();
  const { period, from, to } = siteNowRoute.useSearch();
  return <SiteNow site={site} period={period} from={from} to={to} />;
}

const dataPathRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/datapath",
  component: DataPathScreen,
});

function DataPathScreen() {
  const { site } = dataPathRoute.useParams();
  return <DataPath site={site} />;
}

// P8 — Plant schematic (topology graph, elkjs + SVG).
const schematicRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/schematic",
  component: SchematicScreen,
});

function SchematicScreen() {
  const { site } = schematicRoute.useParams();
  return (
    <Suspense fallback={<p className="text-text-muted">Loading schematic…</p>}>
      <DynamicSLD site={site} />
    </Suspense>
  );
}

// P4 — Meters & sensors (signal tiles + uPlot trends).
const sensorsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/sensors",
  component: SensorsScreen,
});

function SensorsScreen() {
  const { site, tenant, scope } = sensorsRoute.useParams();
  return (
    <Suspense fallback={<p className="text-text-muted">Loading sensors…</p>}>
      <SensorView site={site} tenant={tenant} scope={scope} />
    </Suspense>
  );
}

const kpiRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "kpi",
  validateSearch: validateTimeSearch,
  component: KpiScreen,
});

function KpiScreen() {
  const { scope } = kpiRoute.useParams();
  const { period } = kpiRoute.useSearch();
  return <Kpi scope={scope} period={period} />;
}

// F3 — Loss waterfall at portfolio/scope level.
const waterfallRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "waterfall",
  validateSearch: validateTimeSearch,
  component: WaterfallScreen,
});

function WaterfallScreen() {
  const { scope } = waterfallRoute.useParams();
  // `from`/`to` were validated here all along and then dropped on the floor, so
  // the screen's Custom tab and date arrows had nothing to work with.
  const { period, from, to } = waterfallRoute.useSearch();
  return <PortfolioWaterfall scope={scope} period={period} from={from} to={to} />;
}

const assetsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "assets",
  component: Assets,
});

const dataRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "data",
  component: ScopeDataScreen,
});

function ScopeDataScreen() {
  const { tenant, scope } = dataRoute.useParams();
  return <ScopeData tenant={tenant} scope={scope} />;
}

const adminRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "admin",
  component: AdminScreen,
});

function AdminScreen() {
  const { tenant } = adminRoute.useParams();
  return <Admin tenant={tenant} />;
}

// M1 — Admin: Organisations and relationships (wireframe 05).
const organisationsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "admin/organisations",
  component: () => <Organisations />,
});

// M2 — Admin: People and access (wireframe 06).
const peopleRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "admin/organisations/people",
  component: () => <People />,
});

// M5 — Admin: Configuration packs (wireframe 07). Config resolves per site
// (directive §5.7), so the site is URL state: …/admin/configuration?site=SITE_ID.
// No site is defaulted — the screen says so and offers the server's site list.
const configurationRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "admin/configuration",
  validateSearch: validateConfigurationSearch,
  component: ConfigurationScreen,
});

function ConfigurationScreen() {
  const { tenant, scope } = configurationRoute.useParams();
  const { site } = configurationRoute.useSearch();
  return <Configuration site={site} tenant={tenant} scope={scope} />;
}

// M6 — Admin: Assets and topology onboarding wizard (wireframe 08).
const onboardingRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "admin/assets/onboard",
  component: () => <Onboarding />,
});

// P3 — Site: Blocks (Disha). Block cards ranked by deviation.
const blocksRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/blocks",
  component: BlocksScreen,
});

function BlocksScreen() {
  const { site } = blocksRoute.useParams();
  return <SiteBlocksScreen siteId={site} />;
}

// B3 — Block detail page (Shubham). Own URL beside the existing drawer.
const blockDetailRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/blocks/$blockId",
  validateSearch: validateTimeSearch,
  component: BlockDetailScreen,
});

function BlockDetailScreen() {
  const { site, blockId } = blockDetailRoute.useParams();
  const { period } = blockDetailRoute.useSearch();
  return <BlockDetail site={site} blockId={blockId} period={period} />;
}

// P5 — Site: Strings and combiners (Shubham). String-level heat-map matrix.
const stringsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/strings",
  component: StringsScreen,
});

function StringsScreen() {
  const { site } = stringsRoute.useParams();
  return <SiteStringsScreen siteId={site} />;
}

// P4 — Meters & sensors, explorer mode (Disha). Device tree + parameter stack.
const explorerRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/explorer",
  component: ExplorerScreen,
});

function ExplorerScreen() {
  const { site } = explorerRoute.useParams();
  return <ExplorerView site={site} />;
}

// Q1 — Fleet device-alerts list (Disha B4), scope-level.
const alertsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "alerts",
  component: AlertsFleetScreen,
});

function AlertsFleetScreen() {
  return <AlertsList />;
}

// P6 — Site device-alerts tab (Disha B4), site-level.
const siteAlertsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/alerts",
  component: SiteAlertsScreen,
});

function SiteAlertsScreen() {
  const { site } = siteAlertsRoute.useParams();
  return <AlertsList site={site} />;
}

// Branding / design-token reference (Shubham), scope-level.
const brandingRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "branding",
  component: BrandingScreen,
});

function BrandingScreen() {
  const { scope } = brandingRoute.useParams();
  return <Branding tenant={scope} />;
}

// Actions queue + notifications + command palette (Disha), scope-level.
const interventionsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "interventions",
  component: InterventionsRoute,
});

function InterventionsRoute() {
  const { scope } = interventionsRoute.useParams();
  return <InterventionsScreen scope={scope} />;
}

const notificationsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "notifications",
  component: NotificationsRoute,
});

function NotificationsRoute() {
  const { scope } = notificationsRoute.useParams();
  return <NotificationsScreen scope={scope} />;
}

const searchRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "search",
  component: SearchRoute,
});

function SearchRoute() {
  const { scope } = searchRoute.useParams();
  return <SearchScreen scope={scope} />;
}

// Phase 2-5 family stubs (NeuraMach scope — shown locked per UX-002 §8).
const actionsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "actions",
  component: () => <LockedFamilyScreen family="actions" />,
});

const ledgersRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "ledgers",
  component: () => <LockedFamilyScreen family="ledgers" />,
});

const workRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "work",
  component: () => <LockedFamilyScreen family="work" />,
});

// Locked family stubs for docs, reports, ask (per UX-002 §8).
const docsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "docs",
  component: () => <LockedFamilyScreen family="docs" />,
});

const reportsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "reports",
  component: () => <Reports />,
});

const askRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "ask",
  component: () => <LockedFamilyScreen family="ask" />,
});

// Site-level pages under plants/$site — new routes for the site tab bar.
const sitePerformanceRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/performance",
  validateSearch: validateTimeSearch,
  component: SitePerformanceScreen,
});

function SitePerformanceScreen() {
  const { site } = sitePerformanceRoute.useParams();
  // The route has always validated a custom window; the screen simply never read
  // it, so a range could be in the URL and change nothing.
  const { period, from, to } = sitePerformanceRoute.useSearch();
  return <SitePerformance site={site} period={period} from={from} to={to} />;
}

const siteEquipmentRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/equipment",
  validateSearch: validateEquipmentSearch,
  component: SiteEquipmentScreen,
});

function SiteEquipmentScreen() {
  const { site } = siteEquipmentRoute.useParams();
  // ?equipment=ID deep-links (block "Open", global search) open that equipment
  // directly on Shubham's register page (§20/§09).
  const { equipment } = siteEquipmentRoute.useSearch();
  return <SiteEquipment site={site} initialEquipmentId={equipment} />;
}

const siteWeatherRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/weather",
  component: SiteWeatherScreen,
});

function SiteWeatherScreen() {
  const { site } = siteWeatherRoute.useParams();
  return <SiteWeatherForecast site={site} />;
}

const siteSettingsRoute = createRoute({
  getParentRoute: () => scopeRoute,
  path: "plants/$site/settings",
  component: SiteSettingsScreen,
});

function SiteSettingsScreen() {
  const { site, tenant, scope } = siteSettingsRoute.useParams();
  return <SiteSettings site={site} tenant={tenant} scope={scope} />;
}

// The plain-language glossary (Disha) — global, not scope-bound.
const glossaryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "glossary",
  component: GlossaryScreen,
});

// A6 — User preferences, global (not scope-bound), reached from the top bar.
const preferencesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "preferences",
  component: Preferences,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  glossaryRoute,
  preferencesRoute,
  scopeRoute.addChildren([
    scopeIndexRoute,
    plantsRoute,
    siteNowRoute,
    dataPathRoute,
    schematicRoute,
    sensorsRoute,
    blocksRoute,
    blockDetailRoute,
    stringsRoute,
    explorerRoute,
    alertsRoute,
    siteAlertsRoute,
    brandingRoute,
    kpiRoute,
    waterfallRoute,
    assetsRoute,
    dataRoute,
    adminRoute,
    organisationsRoute,
    peopleRoute,
    configurationRoute,
    onboardingRoute,
    interventionsRoute,
    notificationsRoute,
    searchRoute,
    actionsRoute,
    ledgersRoute,
    workRoute,
    docsRoute,
    reportsRoute,
    askRoute,
    sitePerformanceRoute,
    siteEquipmentRoute,
    siteWeatherRoute,
    siteSettingsRoute,
  ]),
]);
